import { t } from "i18next";
import React, { useImperativeHandle, useMemo, useState } from "react";
import {
  FlatList,
  StyleSheet,
  type TextInput,
  TouchableOpacity,
} from "react-native";
import { View, YStack } from "tamagui";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { Input, type InputProps } from "../../../ui/InputNew";
import { Subtext } from "../../../ui/Subtext";
import type {
  BusinessPlanModel,
  MentionLookup,
  MentionsLookup,
} from "../../plan/BusinessPlanModel";
import { BusinessFeedMentionType } from "../BusinessFeedItem";

type MentionLookupWithName = MentionLookup & { name: string };

export function useMentionAutocomplete(mentionsLookup: MentionsLookup) {
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [triggerChar, setTriggerChar] = useState<"@" | "#" | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const onChangeText = (
    text: string,
    selection: { start: number; end: number },
  ) => {
    const cursorPos = selection.start;

    let keyword: string | undefined;
    let detectedTrigger: "@" | "#" | null = null;

    const characterTyped = text[cursorPos];

    if (characterTyped === "#" || characterTyped === "@") {
      detectedTrigger = characterTyped;
      keyword = "";
    } else {
      // Match both @mentions and #tasks
      const mentionRegex = /([@#])([\p{L}\p{N}\p{M}_ .-]*)/gu;
      mentionRegex.lastIndex = 0;
      let match: RegExpExecArray | null;

      // biome-ignore lint/suspicious/noAssignInExpressions: Because
      while ((match = mentionRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;

        if (cursorPos >= start + 1 && cursorPos <= end) {
          keyword = text.slice(start + 1, cursorPos + 1);
          detectedTrigger = match[1] as "@" | "#";
          break;
        }

        // Cursor right after trigger char with no letters typed
        if (cursorPos === start + 1) {
          keyword = "";
          detectedTrigger = match[1] as "@" | "#";
          break;
        }
      }
    }

    setQuery(keyword ?? null);
    setTriggerChar(detectedTrigger);
    setActive(keyword !== undefined);
  };

  const groupedMatches = useMemo<
    Record<BusinessFeedMentionType, MentionLookupWithName[]>
  >(() => {
    if (query === null) return {};

    const lowered = query.toLowerCase();
    const results: Record<BusinessFeedMentionType, MentionLookupWithName[]> = {
      [BusinessFeedMentionType.User]: [],
      [BusinessFeedMentionType.Group]: [],
      [BusinessFeedMentionType.Schedule]: [],
      [BusinessFeedMentionType.Task]: [],
      [BusinessFeedMentionType.Info]: [],
    };

    for (const name of Object.keys(mentionsLookup)) {
      const { type } = mentionsLookup[name];

      // Only show Tasks when triggered by #
      if (
        (type === BusinessFeedMentionType.Task ||
          type === BusinessFeedMentionType.Schedule) &&
        triggerChar !== "#"
      ) {
        continue;
      }

      if (
        (type === BusinessFeedMentionType.User ||
          type === BusinessFeedMentionType.Group) &&
        triggerChar !== "@"
      ) {
        continue;
      }

      if (name.toLowerCase().includes(lowered)) {
        results[type].push({ ...mentionsLookup[name], name });
      }
    }

    // Filter out empty categories and sort alphabetically
    const sorted = Object.entries(results)
      .filter(([_, arr]) => arr.length > 0)
      .map(([type, arr]) => [
        type,
        arr.sort((a, b) => a.name.localeCompare(b.name)),
      ]);

    return Object.fromEntries(sorted);
  }, [query, triggerChar, mentionsLookup]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [groupedMatches]);

  const onSelectMention = (
    name: string,
    text: string,
    setText: (t: string) => void,
    selection: { start: number; end: number },
  ) => {
    const cursorPos = selection.start;
    const mentionRegex = /([@#])([\p{L}\p{N}\p{M}_ .-]*)/gu;

    let match: RegExpExecArray | null;
    let replaced = false;
    let newText = text;

    // biome-ignore lint/suspicious/noAssignInExpressions: Because
    while ((match = mentionRegex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      if (cursorPos >= start + 1 && cursorPos <= end) {
        newText = `${text.slice(0, start)}${match[1]}${name} ${text.slice(end)}`;
        replaced = true;
        break;
      }

      if (cursorPos === start + 1) {
        newText = `${text.slice(0, start + 1)}${name} ${text.slice(end)}`;
        replaced = true;
        break;
      }
    }

    if (!replaced) {
      // fallback: insert at cursor
      newText = `${text.slice(0, cursorPos)}@${name} ${text.slice(cursorPos)}`;
    }

    setText(newText);
    setQuery(null);
    setTriggerChar(null);
    setActive(false);
  };

  return {
    query,
    active,
    groupedMatches,
    onChangeText,
    onSelectMention,
    highlightedIndex,
  };
}

const TYPE_LABELS = (): Record<BusinessFeedMentionType, string> => ({
  // "@"
  [BusinessFeedMentionType.User]: t("business:mention_people"),
  [BusinessFeedMentionType.Group]: t("business:mention_groups"),

  // "#"
  [BusinessFeedMentionType.Schedule]: t("business:mention_events"),
  [BusinessFeedMentionType.Task]: t("business:mention_tasks"),

  // Not available yet
  [BusinessFeedMentionType.Info]: t("business:mention_info"),
});

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: 62,
    left: 16,
    right: 16,
    maxHeight: 200,
    zIndex: 100,
  },
  section: {},
  header: {},
  item: { width: "100%", flex: 1 },
  text: {},
});

export function MentionSuggestions({
  highlightedIndex,
  groupedMatches,
  onSelect,
  query,
}: {
  highlightedIndex: number;
  groupedMatches: Record<BusinessFeedMentionType, MentionLookupWithName[]>;
  onSelect: (name: string) => void;
  query: string | null;
}) {
  const types = Object.keys(groupedMatches) as BusinessFeedMentionType[];
  if (!types.length) return null;

  const renderHighlighted = (
    mentionData: MentionLookupWithName,
    type: BusinessFeedMentionType,
  ) => {
    const triggerChar =
      type === BusinessFeedMentionType.User ||
      type === BusinessFeedMentionType.Group
        ? "@"
        : "#";

    const name = mentionData.name;

    const description = mentionData.description ? (
      <Body color={"$muted"}>{mentionData.description}</Body>
    ) : null;

    if (!query)
      return (
        <Body>
          {triggerChar}
          {name}
          {description}
        </Body>
      );

    const lowerName = name.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const startIndex = lowerName.indexOf(lowerQuery);
    if (startIndex === -1)
      return (
        <Body>
          {triggerChar}
          {name}
          {description}
        </Body>
      );
    const endIndex = startIndex + query.length;

    return (
      <Body>
        <Body fontWeight={"bold"}>
          {triggerChar}
          {name.slice(0, startIndex)}
          {name.slice(startIndex, endIndex)}
        </Body>
        {name.slice(endIndex)}
        {description}
      </Body>
    );
  };

  const labels = TYPE_LABELS();

  return (
    <Box
      style={styles.container}
      borderColor={"$divider"}
      borderWidth={1}
      noPadding
    >
      <FlatList
        data={types}
        keyboardShouldPersistTaps="handled"
        keyExtractor={(t) => t}
        renderItem={({ item: type }) => (
          <YStack style={styles.section} gap={"$small"}>
            <View
              backgroundColor={"$background"}
              paddingHorizontal={"$space.larger"}
              paddingVertical={"$medium"}
            >
              <Subtext>{labels[type]}</Subtext>
            </View>
            <View
              paddingHorizontal={"$space.larger"}
              paddingVertical={"$medium"}
            >
              <Group borderTop={false} borderBottom={false}>
                {groupedMatches[type].map((mentionData, idx) => (
                  <TouchableOpacity
                    key={mentionData.uid}
                    onPress={() => onSelect(mentionData.name)}
                    style={[
                      styles.item,
                      idx === highlightedIndex && {
                        backgroundColor: "#E6F0FF",
                      }, // highlight
                    ]}
                  >
                    {renderHighlighted(mentionData, type)}
                  </TouchableOpacity>
                ))}
              </Group>
            </View>
          </YStack>
        )}
      />
    </Box>
  );
}

export function FeedTextInput({
  businessPlan,
  onSubmitEditing,
  placeholder,
  placeholderFocused,
  onChangeText,
  ref,
  ...rest
}: {
  businessPlan: BusinessPlanModel;
  placeholderFocused?: string;
  ref?: React.RefObject<{ focus: () => void; clear: () => void } | null>;
} & InputProps) {
  const textInputRef = React.useRef<TextInput | null>(null);
  const [text, setText] = useState("");
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [isFocused, setIsFocused] = useState(false);

  const {
    query,
    groupedMatches,
    onChangeText: autoCompleteOnChangeText,
    onSelectMention,
    highlightedIndex,
  } = useMentionAutocomplete(businessPlan.mentionsLookup);

  const handleSelect = (name: string) => {
    onSelectMention(
      name,
      text,
      (newText) => {
        setText(newText);
        onChangeText?.(newText);
      },
      selection,
    );

    setTimeout(() => {
      textInputRef.current?.focus();
    }, 0);
  };

  useImperativeHandle(ref, () => ({
    focus: () => textInputRef.current?.focus(),
    clear: () => textInputRef.current?.clear(),
  }));

  return (
    <View
      style={{
        flex: 1,
        position: "relative",
      }}
    >
      <MentionSuggestions
        highlightedIndex={highlightedIndex}
        groupedMatches={groupedMatches}
        onSelect={handleSelect}
        query={query}
      />

      <Input
        ref={textInputRef}
        value={text}
        type="padded"
        submitOnEnter={false}
        submitOnBlur={false}
        blurOnSubmit={false}
        clearOnSubmit
        borderBottomWidth={2}
        onChangeText={(t) => {
          autoCompleteOnChangeText(t, selection);
          setText(t);
          onChangeText?.(t);
        }}
        onSubmitEditing={onSubmitEditing}
        onSelectionChange={(e) => {
          setSelection(e.nativeEvent.selection);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onExtraKeyPress={(e) => {
          if (e.nativeEvent.key === "Enter") {
            if (query && Object.keys(groupedMatches).length > 0) {
              // find first actual mention item in your grouped structure
              const firstType = Object.keys(
                groupedMatches,
              )[0] as BusinessFeedMentionType;
              const item = groupedMatches[firstType][highlightedIndex];

              if (item) {
                e.preventDefault?.(); // stops newline on iOS
                e.stopPropagation?.();
                handleSelect(item.name);
                return true;
              }
            }
          }

          return false;
        }}
        placeholder={
          isFocused && placeholderFocused ? placeholderFocused : placeholder
        }
        {...rest}
      />
    </View>
  );
}
